import { useEffect, useState } from 'react'
import { api, type ClinicSettings, type Doctor } from '../api'
import { useAuth } from '../auth'

interface Health { status: string; database: string }

export default function Dashboard() {
  const { user } = useAuth()
  const [settings, setSettings] = useState<ClinicSettings | null>(null)
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [health, setHealth] = useState<Health | null>(null)

  useEffect(() => {
    void (async () => {
      const [s, d, h] = await Promise.allSettled([
        api.get<{ settings: ClinicSettings | null }>('/api/settings'),
        api.get<{ doctors: Doctor[] }>('/api/doctors'),
        api.get<Health>('/api/health'),
      ])
      if (s.status === 'fulfilled') setSettings(s.value.settings)
      if (d.status === 'fulfilled') setDoctors(d.value.doctors)
      if (h.status === 'fulfilled') setHealth(h.value)
    })()
  }, [])

  return (
    <>
      <div className="page-head">
        <h2>Overview</h2>
        <p>
          Who you are signed in as, and how the clinic is configured. The day&rsquo;s work starts
          at Reception; the doctor&rsquo;s console, records and reports are in the sidebar.
        </p>
      </div>

      <div className="panel">
        <h3>Signed in</h3>
        <div className="grid two">
          <div className="field">
            <label>User</label>
            <div>{user?.fullName} <span className="pill on">{user?.role}</span></div>
          </div>
          <div className="field">
            <label>Server</label>
            <div>
              {health
                ? <span className="pill on">database {health.database}</span>
                : <span className="pill warn">checking</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <h3>Clinic</h3>
        {settings ? (
          <div className="grid two">
            <div className="field"><label>Name</label><div>{settings.clinicName}</div></div>
            <div className="field"><label>Phone</label><div>{settings.phone ?? '—'}</div></div>
            <div className="field">
              <label>Address</label>
              <div>{[settings.addressLine1, settings.city, settings.pincode].filter(Boolean).join(', ') || '—'}</div>
            </div>
            <div className="field"><label>OPD timings</label><div>{settings.opdTimings ?? '—'}</div></div>
          </div>
        ) : (
          <div className="empty">No clinic settings yet. An admin can add them under Clinic settings.</div>
        )}
      </div>

      <div className="panel">
        <h3>Doctors on the roster</h3>
        {doctors.length === 0 ? (
          <div className="empty">No doctors added yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Specialisation</th><th>Reg. no.</th>
                  <th>Consultation</th><th>Follow-up</th>
                </tr>
              </thead>
              <tbody>
                {doctors.map((d) => (
                  <tr key={d.id}>
                    <td>{d.fullName}<br /><span className="pill off">{d.qualifications ?? '—'}</span></td>
                    <td>{d.specialization ?? '—'}</td>
                    <td>{d.registrationNo ?? <span className="pill warn">missing</span>}</td>
                    <td className="num">₹{d.consultationFee}</td>
                    <td className="num">₹{d.followUpFee} <span className="pill off">{d.followUpWindowDays}d</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
